import React, { useState, type ReactNode } from 'react';

// ** MUI
import IconButton, { type IconButtonProps } from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip, { type TooltipProps } from '@mui/material/Tooltip';
import FaIconUvilo, { type IconPrefix } from './FaIconUvilo';
import Box from '@mui/material/Box';
import { alpha, styled } from '@mui/material/styles';
import clsx from 'clsx';

interface PowerIconButtonProps extends Omit<IconButtonProps, 'title'> {
  icon: string;
  hoverIcon?: string;
  /** Font Awesome prefix (e.g. `fas` for solid); defaults to theme prefix. */
  iconPrefix?: IconPrefix;
  loading?: boolean;
  title?: ReactNode;
  color?: IconButtonProps['color'];
  danger?: boolean;
  reverse?: boolean;
  dimmed?: boolean;
  padding?: number;
  size?: IconButtonProps['size'];
  sx?: IconButtonProps['sx'];
  tooltipSx?: TooltipProps['sx'];
  onClick?: IconButtonProps['onClick'];
  tooltipProps?: Partial<TooltipProps>;
  tooltipPlacement?: TooltipProps['placement'];
}

const IconButtonStyled = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'buttonPadding',
})<IconButtonProps & { buttonPadding?: number }>(({ theme, buttonPadding }) => ({
  '&.MuiIconButton-root': {
    transition: theme.transitions.create(['background-color', 'color'], {
      duration: theme.transitions.duration.short,
    }),
    padding: buttonPadding !== undefined ? theme.spacing(buttonPadding) : undefined,
    '&.reverse': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
    '&.reverse:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    '&.danger:hover': {
      color: theme.palette.error.main,
      backgroundColor: alpha(theme.palette.error.main, theme.palette.action.selectedOpacity),
    },
    '&.danger.reverse:hover': {
      color: theme.palette.error.contrastText,
      backgroundColor: theme.palette.error.main,
    },
    '&.dimmed': {
      opacity: 0.5,
      transition: [theme.transitions.create(['opacity'])],
      '&:hover': { opacity: 1 },
    },
  },
}));

const TooltipStyled = styled(Tooltip)<TooltipProps>(({ theme }) => ({
  '& .MuiTooltip-tooltipArrow': {
    backgroundColor: theme.palette.error.main,
  },
  '& .MuiTooltip-arrow': {
    color: theme.palette.error.main,
  },
}));

/** MUI keeps a 14px gap even with an arrow; the arrow covers ~8px, so pull the tooltip in until the tip touches the button. */
const TOOLTIP_SLOT_PROPS: TooltipProps['slotProps'] = {
  popper: { modifiers: [{ name: 'offset', options: { offset: [0, -6] } }] },
};

export default function PowerIconButton(props: PowerIconButtonProps) {
  'use memo';

  const {
    icon,
    hoverIcon,
    iconPrefix,
    loading,
    disabled,
    title,
    tooltipPlacement,
    color,
    danger,
    reverse,
    dimmed,
    padding,
    size = 'medium',
    sx,
    tooltipSx,
    className,
    onClick,
    tooltipProps,
    ...other
  } = props;

  const [isActive, setIsActive] = useState(false);
  const displayedIcon = hoverIcon && isActive ? hoverIcon : icon;

  const handleMouseEnter: IconButtonProps['onMouseEnter'] = (event) => {
    other.onMouseEnter?.(event);
    setIsActive(true);
  };
  const handleMouseLeave: IconButtonProps['onMouseLeave'] = (event) => {
    other.onMouseLeave?.(event);
    setIsActive(false);
  };
  const handleFocus: IconButtonProps['onFocus'] = (event) => {
    other.onFocus?.(event);
    setIsActive(true);
  };
  const handleBlur: IconButtonProps['onBlur'] = (event) => {
    other.onBlur?.(event);
    setIsActive(false);
  };

  const iconButton = (
    <IconButtonStyled
      className={clsx(
        'power-icon-button',
        className,
        danger ? 'danger' : '',
        reverse ? 'reverse' : '',
        dimmed ? 'dimmed' : '',
      )}
      buttonPadding={padding}
      disabled={loading || disabled}
      color={color}
      size={size}
      sx={sx}
      onClick={onClick}
      {...other}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '1em',
          height: '1em',
          overflow: 'visible',
          pointerEvents: 'none',
        }}
      >
        {loading ? (
          <CircularProgress size={18} thickness={6} />
        ) : (
          <FaIconUvilo
            icon={displayedIcon}
            prefix={iconPrefix}
            square
            widthAuto
            sx={{
              overflow: 'visible',
              width: 'auto',
              '& svg.svg-inline--fa': {
                width: 'auto',
                height: '100%',
                '--fa-width': 'auto',
              },
            }}
          />
        )}
      </Box>
    </IconButtonStyled>
  );

  if (title) {
    // A disabled button gets no pointer events, so only then does the tooltip need a wrapper.
    const isInert = Boolean(loading || disabled);
    return (
      <TooltipStyled
        title={title}
        placement={tooltipPlacement}
        disableInteractive
        sx={tooltipSx}
        slotProps={TOOLTIP_SLOT_PROPS}
        {...tooltipProps}
        className={clsx('power-icon-button', tooltipProps?.className)}
      >
        {isInert ? <span style={{ display: 'inline-flex' }}>{iconButton}</span> : iconButton}
      </TooltipStyled>
    );
  } else {
    return iconButton;
  }
}
